import React, { useState } from "react";
import { FaCheckCircle, FaTimesCircle, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import "katex/dist/katex.min.css";

const ResultPage = () => {
    const { state } = useLocation();
    const { result = [] } = state || {}; // ensure result is always array
    const navigate = useNavigate();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedSubject, setSelectedSubject] = useState("All");
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Filter results by subject
    const subjects = ["All", ...new Set(result.map((r) => r.subject))];
    const filteredResults =
        selectedSubject === "All"
            ? result
            : result.filter((r) => r.subject === selectedSubject);

    const totalQuestions = filteredResults.length;

    // Unified handling of old (quiz) and new formats
    const currentQuestion = (() => {
        const item = filteredResults[currentIndex] || {};
        return item.quiz && Object.keys(item.quiz).length > 0 ? { ...item.quiz, ...item } : item;
    })();

    const selectedOption = currentQuestion.selected_option;
    const isCorrect = currentQuestion.is_correct === 1;

    const score = filteredResults.filter((r) => r.is_correct === 1).length;
    const percentage = totalQuestions > 0 ? ((score / totalQuestions) * 100).toFixed(2) : 0;

    const mathJaxConfig = {
        loader: { load: ["input/asciimath", "output/chtml"] },
        tex: {
            inlineMath: [["$", "$"], ["\\(", "\\)"]],
            displayMath: [["$$", "$$"], ["\\[", "\\]"]],
        },
    };

    // Detects any math delimiter, including single-dollar `$...$` inline math.
    const HAS_MATH = /\$|\\\(|\\\[/;

    // Render bold + MathJax. We split only on **bold** and hand each remaining
    // chunk to MathJax as-is, so MathJax itself typesets every delimiter
    // ($...$, $$...$$, \(...\), \[...\]) per the config. (The old code matched
    // only $$ and \(, so single-`$` math rendered as literal dollar signs.)
    const renderTextWithMath = (raw) => {
        const text =
            raw == null ? "" : (typeof raw === "string" || typeof raw === "number" ? String(raw) : JSON.stringify(raw));
        if (!text) return <span> </span>;
        return text.split(/(\*\*[\s\S]*?\*\*)/g).map((part, idx) => {
            if (!part) return null;
            if (part.length >= 4 && part.startsWith("**") && part.endsWith("**")) {
                return <strong key={idx}>{part.slice(2, -2)}</strong>;
            }
            if (HAS_MATH.test(part)) {
                return <MathJax key={idx} inline dynamic>{part}</MathJax>;
            }
            return <span key={idx}>{part}</span>;
        });
    };

    const handleNext = () => {
        if (currentIndex < totalQuestions - 1) setCurrentIndex(currentIndex + 1);
    };

    const handleBack = () => {
        if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    };

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    const handleSubjectChange = (subject) => {
        setSelectedSubject(subject);
        setCurrentIndex(0);
    };

    const goBack = () => navigate(-1);

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="flex flex-col min-h-screen bg-gray-100">

                {/* Header */}
                <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-6">
                    <h1 className="text-2xl font-bold text-white">Exam Results</h1>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-white text-sm sm:text-base">
                        <span className="px-2 py-1 bg-blue-700 rounded-md">
                            Total Questions: <strong>{totalQuestions}</strong>
                        </span>
                        <span className="px-2 py-1 bg-green-600 rounded-md">
                            Score: <strong>{score}</strong>
                        </span>
                        <span
                            className={`px-2 py-1 rounded-md ${percentage >= 50 ? "bg-green-500" : "bg-red-500"}`}
                        >
                            Percentage: <strong>{percentage}%</strong>
                        </span>
                    </div>
                </nav>

                {/* Subject Filter */}
                <div className="p-4 sm:p-6 bg-white shadow-md">
                    <div className="flex gap-4 overflow-x-auto">
                        {subjects.map((subject, idx) => (
                            <button
                                key={idx}
                                className={`px-4 py-2 rounded-lg font-semibold ${selectedSubject === subject ? "bg-blue-500 text-white" : "bg-gray-200"
                                    }`}
                                onClick={() => handleSubjectChange(subject)}
                            >
                                {subject}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Back Button */}
                <div className="flex items-center mt-6 ml-3 mb-6">
                    <button
                        onClick={goBack}
                        className="flex items-center gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition"
                    >
                        <FaArrowLeft className="text-xl" /> Back
                    </button>
                </div>

                <div className="flex-grow flex flex-col-reverse sm:flex-row">
                    {/* Main Content */}
                    <main className="flex-1 p-4 sm:p-6 bg-white shadow-lg rounded-lg mx-4 sm:mx-6 overflow-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg sm:text-xl font-bold text-[#2148C0]">
                                Question {currentIndex + 1} / {totalQuestions}
                            </h2>
                            <button
                                onClick={toggleSidebar}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                            >
                                {isSidebarOpen ? "Hide" : "Show"} Sidebar
                            </button>
                        </div>

                        {/* Question */}
                        <div className="result-rich bg-gray-50 p-4 sm:p-6 rounded-lg shadow-md">
                            {/* Keep long equations inside their box: wrap normal
                                text, and let wide inline math scroll horizontally. */}
                            <style>{`
                                .result-rich { overflow-wrap: anywhere; word-break: break-word; }
                                .result-rich mjx-container,
                                .result-rich .mjx-container { max-width: 100%; overflow-x: auto; overflow-y: hidden; }
                            `}</style>
                            <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
                                {renderTextWithMath(currentQuestion.question)}
                            </h3>

                            {/* Options */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {["a", "b", "c", "d"].map((opt, idx) => {
                                    const optValue = currentQuestion[`option_${opt}`];
                                    if (!optValue) return null;

                                    const isOptionCorrect = (idx + 1).toString() === currentQuestion.correctAnswer;
                                    const isOptionSelected = selectedOption === opt.toUpperCase();

                                    return (
                                        <div
                                            key={idx}
                                            className={`p-4 rounded-lg shadow-md flex items-center justify-between gap-2 border ${isOptionCorrect
                                                    ? "border-green-500 bg-green-50"
                                                    : isOptionSelected
                                                        ? "border-red-500 bg-red-50"
                                                        : "border-gray-300"
                                                }`}
                                        >
                                            <span className="min-w-0 break-words">
                                                {opt.toUpperCase()}. {renderTextWithMath(optValue)}
                                            </span>
                                            {isOptionCorrect && <FaCheckCircle className="shrink-0 text-green-500 text-xl" />}
                                            {isOptionSelected && !isOptionCorrect && (
                                                <FaTimesCircle className="shrink-0 text-red-500 text-xl" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Unanswered */}
                            {!selectedOption && (
                                <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
                                    <h4 className="font-semibold text-yellow-600">Option Not Selected</h4>
                                    <p>Please review this question and its explanation carefully.</p>
                                </div>
                            )}

                            {/* Explanation */}
                            {currentQuestion.explanation && (
                                <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg overflow-x-auto">
                                    <h4 className="font-semibold text-[#2148C0]">Explanation:</h4>
                                    <p className="break-words whitespace-pre-wrap">{renderTextWithMath(currentQuestion.explanation)}</p>
                                </div>
                            )}
                        </div>

                        {/* Navigation */}
                        <div className="flex justify-between mt-6">
                            <button
                                onClick={handleBack}
                                disabled={currentIndex === 0}
                                className={`px-4 py-2 rounded-lg ${currentIndex > 0
                                        ? "bg-[#2148C0] text-white hover:bg-blue-600"
                                        : "bg-gray-300 text-gray-500"
                                    }`}
                            >
                                <FaArrowLeft className="inline mr-2" /> Back
                            </button>
                            <button
                                onClick={handleNext}
                                disabled={currentIndex === totalQuestions - 1}
                                className={`px-4 py-2 rounded-lg ${currentIndex < totalQuestions - 1
                                        ? "bg-[#2148C0] text-white hover:bg-blue-600"
                                        : "bg-gray-300 text-gray-500"
                                    }`}
                            >
                                Next <FaArrowRight className="inline ml-2" />
                            </button>
                        </div>
                    </main>

                    {/* Sidebar */}
                    {isSidebarOpen && (
                        <aside className="absolute sm:relative w-full sm:w-64 bg-white shadow-lg p-4 border-l z-30">
                            <h3 className="text-lg font-bold mb-4">Attempted Questions</h3>
                            <div className="grid grid-cols-8 sm:grid-cols-4 gap-2">
                                {filteredResults.map((_, idx) => {
                                    const isSelected = currentIndex === idx;
                                    const isCorrectAttempt = filteredResults[idx]?.is_correct === 1;

                                    return (
                                        <button
                                            key={idx}
                                            onClick={() => setCurrentIndex(idx)}
                                            className={`w-12 h-12 flex items-center justify-center rounded-lg font-bold ${isCorrectAttempt
                                                    ? "bg-green-200 text-green-800"
                                                    : "bg-red-50 text-red-800"
                                                } ${isSelected ? "ring-2 ring-blue-500" : ""}`}
                                        >
                                            {idx + 1}
                                        </button>
                                    );
                                })}
                            </div>
                        </aside>
                    )}
                </div>
            </div>
        </MathJaxContext>
    );
};

export default ResultPage;
